import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../../components/layout/index";
import { CalendarDays, Filter, UserRound, ArrowRight, CalendarCheck, Stethoscope, FileText, BadgeCheck, Clock3, ChevronDown } from "lucide-react";



const CONSULTATION_TYPES = ["All", "Video", "In-person"];
const LEAVE_TYPES = ["All", "Planned", "Emergency"];

const CurebayLeave = () => {
  const API_URL = process.env.REACT_APP_NEW_DOCTOR_URL;
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth() + 1
  ); // 1-12
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("All"); // All / applied / Cancelled / Hold
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [leaveApiRows, setLeaveApiRows] = useState([]);

  // Fetch leave list for current month/year on mount
  useEffect(() => {
    const fetchLeaves = async () => {
      try {
        const params = new URLSearchParams({
          month: String(selectedMonth),
          year: String(selectedYear),
        });
        if (statusFilter && statusFilter !== "All") {
          params.set("status", statusFilter);
        }
        const url = `${API_URL}/Leave/list?${params.toString()}`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        console.log("datajhbjh", data);

        // API may return either leaveRecords[] or data[] (appointments list)
        const records = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.leaveRecords)
          ? data.leaveRecords
          : [];

        const mapped = records.map((rec) => {
          // If it's an appointments object (has whenAppointment)
          const isAppointment = !!rec.whenAppointment;

          if (isAppointment) {
            const when = rec.whenAppointment || "";
            const rawDate = when ? when.split("T")[0] : "";
            const datePart = rawDate ? rawDate.replaceAll("-", "/") : "";
            const from = rec.fromTime || "";
            const to = rec.toTime || "";

            const doctorFullName = `${rec.doctorSalutation || ""} ${
              rec.doctorFirstName || ""
            } ${rec.doctorLastName || ""}`.trim();

            return {
              id: rec.id,
              doctorName: doctorFullName || rec.userId || "Doctor",
              speciality: rec.doctorSpeciality || "",
              // leaveType: "Hold",
              // appointment status is numeric in many cases; map hold(16) to label
              status: rec.status === 16 ? "Hold" : String(rec.status ?? ""),
              consultationType:
                rec.consultationsType === "V" ? "Video" : "In-person",
              leaveDates:
                datePart && from && to
                  ? `${datePart} ${from} - ${to}`
                  : datePart || "-",
              bookedSlots: 1,
              compensationSlots: "-",
              scheduledSlotsUrl: "#",
            };
          }

          // Fallback to original Leave/list mapping
          const rawFrom = rec.fromDate?.split(" ")[0] || "";
          const rawTo = rec.toDate?.split(" ")[0] || "";
          const from = rawFrom ? rawFrom.replaceAll("-", "/") : "";
          const to = rawTo ? rawTo.replaceAll("-", "/") : "";
          return {
            id: rec.id,
            doctorName: rec.doctorName || "Doctor",
            speciality: "", // not provided by API
            leaveType: rec.status || "Planned",
            status: String(rec.status ?? ""),
            consultationType: "Video", // placeholder
            leaveDates: from && to ? `${from} - ${to}` : from || to || "-",
            bookedSlots: rec.noLeaveDays ?? "-",
            compensationSlots: "-", // not provided
            scheduledSlotsUrl: "#",
            reason: rec.reason || "-",
          };
        });

        setLeaveApiRows(mapped);
      } catch (error) {
        console.error("Failed to fetch leave list:", error);
      }
    };

    fetchLeaves();
  }, [API_URL, selectedMonth, selectedYear, statusFilter]);

  const filteredRows = useMemo(() => {
    const sourceRows = leaveApiRows || [];
    if (statusFilter === "All") return sourceRows;
    return sourceRows.filter(
      (row) => (row.status || "").toLowerCase() === statusFilter.toLowerCase()
    );
  }, [leaveApiRows, statusFilter]);

  const totalElements = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pageRows = filteredRows.slice(startIndex, startIndex + pageSize);
  const startEntry = totalElements === 0 ? 0 : startIndex + 1;
  const endEntry = Math.min(startIndex + pageSize, totalElements);

  const handleExport = () => {
    // Placeholder – real implementation would call backend to export
    // eslint-disable-next-line no-alert
    alert("Export to Excel will be implemented with backend support.");
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const visiblePages = () => {
    const pages = [];
    const maxVisible = 7;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    } else if (safePage <= 4) {
      for (let i = 1; i <= maxVisible; i += 1) pages.push(i);
    } else if (safePage >= totalPages - 3) {
      for (let i = totalPages - maxVisible + 1; i <= totalPages; i += 1) {
        pages.push(i);
      }
    } else {
      for (let i = safePage - 3; i <= safePage + 3; i += 1) pages.push(i);
    }
    return pages;
  };

  const toLower = (value) => String(value || "").toLowerCase();
  const isApplied = (value) => toLower(value) === "applied";
  const isCancelled = (value) => toLower(value) === "cancelled";

  const leaveTypeBadgeClass = (value) =>
    isApplied(value)
      ? "bg-emerald-50 text-emerald-700"
      : "bg-rose-50 text-rose-600";

  const statusBadgeClass = (value) =>
    isApplied(value)
      ? "bg-emerald-50 text-emerald-700"
      : isCancelled(value)
      ? "inline-block text-sm px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-medium"
      : "bg-amber-50 text-amber-700";

  return (
    <DashboardLayout>
      <div className="mt-6 px-4">
      <div className="flex items-center gap-4 mb-8">
            <div className="bg-emerald-50 p-2 rounded-xl border border-emerald-100 flex items-center justify-center">
              <CalendarCheck size={26} className="text-emerald-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#334155] tracking-tight">
              Leave List
              </h1>
            </div>
          </div>
        {/* Filters */}
        <div className="mb-5 rounded-2xl border border-slate-100 bg-white px-4 py-4 shadow-sm sm:px-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                Month / Year
              </label>
              <div className="flex items-center gap-2">
                <div className="relative w-[118px]">
                  <CalendarDays
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"
                  />
                  <select
                    value={selectedMonth}
                    onChange={(e) => {
                      setSelectedMonth(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="h-10 w-full appearance-none rounded-xl border border-slate-200 bg-white pl-9 pr-8 text-sm font-medium text-slate-700 focus:border-teal-500/50 focus:outline-none"
                  >
                    <option value={1}>Jan</option>
                    <option value={2}>Feb</option>
                    <option value={3}>Mar</option>
                    <option value={4}>Apr</option>
                    <option value={5}>May</option>
                    <option value={6}>Jun</option>
                    <option value={7}>Jul</option>
                    <option value={8}>Aug</option>
                    <option value={9}>Sep</option>
                    <option value={10}>Oct</option>
                    <option value={11}>Nov</option>
                    <option value={12}>Dec</option>
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                </div>
                <span className="text-slate-300">/</span>
                <div className="relative w-[128px]">
                  <CalendarDays
                    size={14}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"
                  />
                  <select
                    value={selectedYear}
                    onChange={(e) => {
                      setSelectedYear(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="h-10 w-full appearance-none rounded-xl border border-slate-200 bg-white pl-9 pr-8 text-sm font-medium text-slate-700 focus:border-teal-500/50 focus:outline-none"
                  >
                    {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-1 lg:w-[250px]">
              <label className="text-[11px] font-black uppercase tracking-widest text-slate-400">
                Status
              </label>
              <div className="relative">
                <Filter
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-teal-600"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="h-10 w-full appearance-none rounded-xl border border-slate-200 bg-white pl-9 pr-8 text-sm font-medium text-slate-700 focus:border-teal-500/50 focus:outline-none"
                >
                  <option value="All">All</option>
                  <option value="applied">Applied</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
                <ChevronDown
                  size={14}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
              </div>
            </div>
          </div>
        </div>

        

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-sky-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                    <Stethoscope size={14} className="text-teal-600" />
                    
                      Doctor Name
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-emerald-600" />
                      Reason for Leave
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <BadgeCheck size={14} className="text-emerald-600" />
                      Status
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <CalendarDays size={14} className="text-emerald-600" />
                      Leave Dates
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-600">
                    <div className="flex items-center gap-2">
                      <Clock3 size={14} className="text-emerald-600" />
                      No of Leave Days
                    </div>
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600">
                    Compensation Slots
                  </th> */}
                  {/* <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600">
                    Scheduled Slots
                  </th> */}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {pageRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No leave records found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((row, index) => (
                    <tr
                      key={row.id}
                      className={index % 2 === 0 ? "bg-white" : "bg-slate-50/50"}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full  flex items-center justify-center">
                          <div className="h-5 w-5 rounded-lg bg-teal-600 flex items-center justify-center shadow flex-shrink-0">
                                <Stethoscope size={12} className="text-white" />
                              </div>
                          </div>
                          <div className="text-sm font-medium text-slate-900">
                            {row.doctorName}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                         
                          <div className="text-sm font-medium text-slate-900">
                            {row.reason}
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        <span
                          className={`inline-flex items-center gap-2 rounded-xl px-3 py-1 text-sm font-medium ${statusBadgeClass(
                            row.status
                          )}`}
                        >
                          
                          {row.status || "-"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {(() => {
                          const [start = "-", end = "-"] = String(
                            row.leaveDates || "-"
                          ).split(" - ");
                          return (
                            <div className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                              {/* <span className="h-8 w-8 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center">
                                <CalendarDays size={14} />
                              </span> */}
                              <span>{start}</span>
                              <ArrowRight size={14} className="text-slate-400" />
                              <span className="">{end}</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 text-center">
                        <span className="inline-flex rounded-lg bg-slate-100 px-3 py-1 text-sm  text-slate-600">
                          {row.bookedSlots} Day{Number(row.bookedSlots) > 1 ? "s" : ""}
                        </span>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">
                        {row.compensationSlots}
                      </td> */}
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-[#2563EB]">
                        <button
                          type="button"
                          onClick={() => {
                            // Placeholder for scheduled slots navigation
                            // eslint-disable-next-line no-alert
                            alert("Scheduled slots view will be implemented.");
                          }}
                          className="underline hover:text-[#1d4ed8]"
                        >
                          View
                        </button>
                      </td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-white text-sm text-slate-600">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage === 1}
                className={`px-2 py-1 text-sm ${
                  safePage === 1
                    ? "text-slate-400 cursor-not-allowed"
                    : "text-slate-700 hover:text-slate-900"
                }`}
              >
                &lt; Prev
              </button>
              <div className="flex items-center gap-1">
                {visiblePages().map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => goToPage(p)}
                    className={`h-8 min-w-8 px-2 rounded-lg border text-sm ${
                      p === safePage
                        ? "border-blue-300 bg-blue-50 text-blue-700"
                        : "border-slate-300 text-slate-600 hover:text-slate-800"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage === totalPages}
                className={`px-2 py-1 text-sm ${
                  safePage === totalPages
                    ? "text-slate-400 cursor-not-allowed"
                    : "text-slate-700 hover:text-slate-900"
                }`}
              >
                Next &gt;
              </button>
            </div>
            <div className="text-xs sm:text-sm text-slate-500">
              Showing {startEntry} to {endEntry} of {totalElements} entries
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CurebayLeave;